import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { z } from "zod";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import FormInputField from "@/components/lib/from-input-field";
import useAuth from "@/hooks/useAuth";
import {
  createNewVariant,
  clearVariantMessages,
} from "@/redux/slice/essential-menu-slice/variants-slice";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";

export default function CreateVariantDialog({
  crust = false,
  isOpen,
  onClose,
  title,
  menu_id,
  item_id,
  item_type = "pizza",
  name_field,
  onSuccess,
}) {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { createLoading, requestStatus, error } = useSelector(
    (state) => state.essentialMenu.variants
  );
  const { crusts } = useSelector((state) => state.essentialMenu.crusts);

  const [showInPrice, setShowInPrice] = useState(false);
  const [showOutPrice, setShowOutPrice] = useState(false);

  const form = useForm({
    resolver: zodResolver(variantSchema),
    mode: "onChange",
    defaultValues: {
      crust_id: null,
      name: "",
      calorie_amount: "",
      in_price: 0,
      out_price: 0,
    },
  });

  const onSubmit = async (data) => {
    const { showInPrice, showOutPrice, ...rest } = data;
    const payload = {
      ...rest,
      menu_item_id: menu_id,
      client_id: user?.client_id,
      restaurant_id: user?.restaurant_id,
      item_id: item_id,
      item_type: item_type,
    };
    try {
      dispatch(clearVariantMessages());
      const res = await dispatch(createNewVariant(payload));
      if (createNewVariant.fulfilled.match(res)) {
          if (onSuccess) onSuccess(res?.payload?.id);
        toast.success("New variant created successfull.");
        form.reset();
        dispatch(clearVariantMessages());
        onClose();
      } else if (createNewVariant.rejected.match(res)) {
        toast.error(res.payload || error);
      }
    } catch (error) {
      toast.error("Something went wrong.");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <div className="py-5 flex flex-col">
          <DialogTitle className="text-lg absolute top-4 font-medium">
            {title || "Create New Variant"}
          </DialogTitle>
          <Separator className="mt-4" />

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-6 mt-5"
            >
              {crust && (
                <FormInputField
                  name="crust_id"
                  style="bg-cblack3 flex-1"
                  placeholder={"Select Crust"}
                  form={form}
                  type="select"
                  parseAs="number"
                  selectOption={
                    crusts && crusts.length > 0
                      ? crusts.filter((item) => item?.status === "active").map((item) => ({
                          label: item.name,
                          value: item.id,
                        }))
                      : [
                          {
                            label: "No Crust available",
                            value: "",
                            disabled: true,
                          },
                        ]
                  }
                  label={"Crust"}
                />
              )}
              <FormInputField
                name={"name"}
                style="bg-cblack3"
                placeholder={`${crust ? "0 inch" : "Variant Name"}`}
                form={form}
                label={`${crust ? "Size" : `${name_field || "Variant Name"}`}`}
                type="text"
              />

              <FormInputField
                name="calorie_amount"
                style="bg-cblack3"
                placeholder={"Calorie Amount"}
                form={form}
                label={"Calorie Amount "}
                type="text"
              />

              {/* In-Price Toggle */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={showInPrice}
                    onCheckedChange={(val) => {
                      setShowInPrice(val);
                      form.setValue("showInPrice", val);
                    }}
                  />
                  <Label>In-Price</Label>
                </div>
                {showInPrice && (
                  <FormInputField
                    name="in_price"
                    placeholder={"Enter amount"}
                    form={form}
                    type="text"
                    style="bg-cblack3"
                  />
                )}
              </div>

              {/* Out-Price Toggle */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={showOutPrice}
                    onCheckedChange={(val) => {
                      setShowOutPrice(val);
                      form.setValue("showOutPrice", val);
                    }}
                  />
                  <Label>Out-Price</Label>
                </div>
                {showOutPrice && (
                  <FormInputField
                    name="out_price"
                    placeholder={"Enter amount"}
                    form={form}
                    type="text"
                    style="bg-cblack3"
                  />
                )}
              </div>

              <Button
                isLoading={createLoading}
                type="submit"
                className="bg-cyellow w-full text-cblack"
              >
                Confirm
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const variantSchema = z
  .object({
    name: z.string().min(1, "Variant name is required"),

    calorie_amount: z.preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z.number({
        required_error: "Calorie amount is required",
        invalid_type_error: "Calorie amount must be a number",
      })
    ),
    crust_id: z
      .preprocess(
        (val) => {
          if (val === "" || val == null) return null;
          return Number(val);
        },
        z
          .number({
            invalid_type_error: "Item no must be a number",
          })
          .nullable()
      )
      .optional(),

    in_price: z
      .preprocess(
        (val) => (val === "" || val == null ? 0 : Number(val)),
        z.number({
          required_error: "In-Price is required",
          invalid_type_error: "In-Price must be a number",
        })
      )
      .optional(),

    out_price: z
      .preprocess(
        (val) => (val === "" || val == null ? 0 : Number(val)),
        z.number({
          required_error: "Out-Price is required",
          invalid_type_error: "Out-Price must be a number",
        })
      )
      .optional(),
  })

  .refine(
    (data) => !data.showInPrice || (data.showInPrice && data.in_price > 0),
    {
      path: ["in_price"],
      message: "In-Price must be greater than 0",
    }
  )
  .refine(
    (data) => !data.showOutPrice || (data.showOutPrice && data.out_price > 0),
    {
      path: ["out_price"],
      message: "Out-Price must be greater than 0",
    }
  );
